// Update generated types with yarn graphql:types static/bundles/promotions/components/Queries.ts
import type { OperationOption } from 'react-apollo';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { ONDEMAND_SPECIALIZATIONS_V1 } from 'bundles/browse/constants';
import { SPECIALIZATION_SUBSCRIPTION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';
import type {
  PromotionDetailsQuery_PromotionDetailsV1Resource_byPromoCodeId_elements as PromotionDetails,
  PromotionDetailsQuery as PromotionDetailsQueryType,
  PromotionDetailsQueryVariables,
} from 'bundles/promotions/components/__generated__/PromotionDetailsQuery';
import GetCoursesForPromotionLandingPage from 'bundles/promotions/queries/GetCoursesForPromotionLandingPageQuery.graphql';
import GetSpecializationsForPromotionLandingPage from 'bundles/promotions/queries/GetSpecializationsForPromotionLandingPageQuery.graphql';
import type {
  CourseForPromotionLandingPageFragment,
  GetCoursesForPromotionLandingPageQuery,
  GetCoursesForPromotionLandingPageQueryVariables,
} from 'bundles/promotions/queries/__generated__/GetCoursesForPromotionLandingPageQuery';
import type {
  GetSpecializationsForPromotionLandingPageQuery,
  GetSpecializationsForPromotionLandingPageQueryVariables,
  SpecializationForPromotionLandingPageFragment,
} from 'bundles/promotions/queries/__generated__/GetSpecializationsForPromotionLandingPageQuery';
import type { ProductCollection, PromotionData } from 'bundles/promotions/types/Promotions';
import { NormalS12n, ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';
import inServerContext from 'bundles/ssr/util/inServerContext';

export const NUM_COLLECTIONS_TO_LOAD_SSR = 1;

const getProductIds = (collections: Array<ProductCollection>): { s12nIds: Array<string>; courseIds: Array<string> } => {
  const s12nIds: Array<string> = [];
  const courseIds: Array<string> = [];

  collections.forEach(({ products }) =>
    products.forEach(({ productId, productType }) => {
      switch (productType) {
        case VERIFIED_CERTIFICATE:
          courseIds.push(productId);
          break;
        case SPECIALIZATION_SUBSCRIPTION:
          s12nIds.push(productId);
          break;
        default:
          break;
      }
    })
  );

  return { s12nIds, courseIds };
};

export type Course = CourseForPromotionLandingPageFragment;
export type Specialization = SpecializationForPromotionLandingPageFragment;

const mapCourseForProductData = (course: Course) => {
  return {
    ...course,
    photoUrl: course.promoPhotoUrl,
    partners: {
      elements: course.partners,
    },
  };
};

const mapSpecializationForProductData = (s12n: Specialization) => {
  return {
    ...s12n,
    __typename: ONDEMAND_SPECIALIZATIONS_V1,
    logo: s12n.cardImageUrl,
    productVariant: s12n.productVariant === 'PROFESSIONAL_CERTIFICATE_S12N' ? ProfessionalCertificateS12n : NormalS12n,
    partners: {
      elements: s12n.partners,
    },
    courseIds: s12n.courses.map(({ id }) => id),
  };
};

export const useGetProductDataForPromotion = (promotionData?: PromotionData, clientPromotionData?: PromotionData) => {
  const ssrCollections = (promotionData?.collections ?? []).slice(0, NUM_COLLECTIONS_TO_LOAD_SSR);
  const { s12nIds: ssrS12nIds, courseIds: ssrCourseIds } = getProductIds(ssrCollections);
  const csrCollectionsAll = clientPromotionData?.collections ?? [];
  const csrCollections = csrCollectionsAll.slice(NUM_COLLECTIONS_TO_LOAD_SSR, csrCollectionsAll.length);
  const { s12nIds: csrS12nIds, courseIds: csrCourseIds } = getProductIds(csrCollections);

  const { data: ssrCourseData, loading: loadingSsrCourses } = useQuery<
    GetCoursesForPromotionLandingPageQuery,
    GetCoursesForPromotionLandingPageQueryVariables
  >(GetCoursesForPromotionLandingPage, {
    skip: ssrCourseIds.length === 0,
    variables: { ids: ssrCourseIds },
    context: { clientName: 'gatewayGql' },
  });

  const ssrCourses = ssrCourseData?.Course?.queryByIds ?? [];

  const { data: ssrS12nData, loading: loadingSsrS12ns } = useQuery<
    GetSpecializationsForPromotionLandingPageQuery,
    GetSpecializationsForPromotionLandingPageQueryVariables
  >(GetSpecializationsForPromotionLandingPage, {
    skip: ssrS12nIds.length === 0,
    variables: { ids: ssrS12nIds },
    context: { clientName: 'gatewayGql' },
  });

  const ssrS12ns = ssrS12nData?.Specialization?.queryByIds ?? [];

  const { data: csrCourseData, loading: loadingCsrCourses } = useQuery<
    GetCoursesForPromotionLandingPageQuery,
    GetCoursesForPromotionLandingPageQueryVariables
  >(GetCoursesForPromotionLandingPage, {
    skip: inServerContext || csrCourseIds.length === 0,
    variables: { ids: csrCourseIds },
    context: { clientName: 'gatewayGql' },
  });

  const csrCourses = csrCourseData?.Course?.queryByIds ?? [];

  const { data: csrS12nData, loading: loadingCsrS12ns } = useQuery<
    GetSpecializationsForPromotionLandingPageQuery,
    GetSpecializationsForPromotionLandingPageQueryVariables
  >(GetSpecializationsForPromotionLandingPage, {
    skip: inServerContext || csrS12nIds.length === 0,
    variables: { ids: csrS12nIds },
    context: { clientName: 'gatewayGql' },
  });

  const csrS12ns = csrS12nData?.Specialization?.queryByIds ?? [];

  return {
    loadingServerProducts: loadingSsrCourses || loadingSsrS12ns,
    loadingClientProducts: loadingCsrCourses || loadingCsrS12ns,
    productsServerData: {
      courses: ssrCourses.map(mapCourseForProductData),
      specializations: ssrS12ns.map(mapSpecializationForProductData),
    },
    productsClientData: {
      courses: csrCourses.map(mapCourseForProductData),
      specializations: csrS12ns.map(mapSpecializationForProductData),
    },
  };
};

export const PromotionDetailsQuery = gql`
  query PromotionDetailsQuery($promoCode: String!) {
    PromotionDetailsV1Resource {
      byPromoCodeId(id: $promoCode) {
        elements {
          id
          startsAt
          endsAt
          promotionDescription
          isTest
        }
      }
    }
  }
`;

type PropsToPromotionDetailsQuery = {
  promoCode: string;
};

export type PropsFromPromotionDetailsQuery = {
  promotionDetails?: PromotionDetails;
};

export const promotionDetailsQueryOptions: OperationOption<
  PropsToPromotionDetailsQuery,
  PromotionDetailsQueryType,
  PromotionDetailsQueryVariables,
  PropsFromPromotionDetailsQuery
> = {
  skip: ({ promoCode }: PropsToPromotionDetailsQuery) => !promoCode,
  options: ({ promoCode }) => ({
    variables: { promoCode },
  }),
  props: ({ data }) => ({
    promotionDetails: data?.PromotionDetailsV1Resource?.byPromoCodeId?.elements?.[0] ?? undefined,
  }),
};

export const ProductSkuQuery = gql`
  query ProductSkuQuery($s12nId: String!) {
    ProductSkusV1Resource {
      findByUnderlying(id: $s12nId) {
        elements {
          id
          productItemId
          productType
          isActive
          properties {
            ... on ProductSkusV1_org_coursera_product_SubscriptionProductPropertiesMember {
              org_coursera_product_SubscriptionProductProperties {
                billingCycle
              }
            }
          }
        }
      }
    }
  }
`;

export const SpecializationsQuery = gql`
  query SpecializationsQuery($s12nId: String!) {
    OnDemandSpecializationsV1Resource {
      get(id: $s12nId) {
        id
        slug
      }
    }
  }
`;

export const CoursesQuery = gql`
  query CourseQuery($courseId: String!) {
    CoursesV1Resource {
      get(id: $courseId) {
        id
        slug
        s12nIds
      }
    }
  }
`;
